import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { SAOP_MODAL } from '../sandboxCopy';

interface SaopInterimModalProps {
  onContinue: () => void;
}

/** Closest available icons to the graphic's review → examine → treat →
 *  document glyphs; swap when CJ supplies final assets. */
const WORKFLOW_ICONS = [Icons.speechBubble, Icons.eye, Icons.dumbell, Icons.compose];

const LetterChip: React.FC<{ letter: string; highlight?: boolean }> = ({ letter, highlight }) => (
  <span
    className={`flex items-center justify-center rounded-full font-bold ${
      highlight
        ? 'h-12 w-12 border-2 border-primary-500 bg-primary-50 text-lg text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
        : 'h-10 w-10 bg-gray-100 text-base text-gray-700 ring-1 ring-inset ring-gray-200 dark:bg-dark-gray-700 dark:text-gray-200 dark:ring-dark-gray-600'
    }`}
  >
    {letter}
  </span>
);

/**
 * Interim transition after Review Subjective, rebuilt to the tester-provided
 * graphic (note #10, 2026-09-08): the clinical-workflow icon row, the
 * "already know the diagnosis" banner, and the S→A→O→P strip with the
 * highlighted Assessment. Informational only — the X and the CTA both simply
 * continue into Select Diagnosis.
 */
const SaopInterimModal: React.FC<SaopInterimModalProps> = ({ onContinue }) => (
  <div
    className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4"
    role="dialog"
    aria-modal="true"
    aria-label={`${SAOP_MODAL.titlePre}${SAOP_MODAL.titleEmphasis}${SAOP_MODAL.titlePost}`}
  >
    <div className="absolute inset-0 bg-gray-900/50" />
    <div className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-6 sm:p-8 text-center shadow-xl dark:bg-dark-gray-800">
      <button
        type="button"
        aria-label="Close"
        onClick={onContinue}
        className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      >
        ✕
      </button>

      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
        {SAOP_MODAL.titlePre}
        <span className="text-primary-600 dark:text-primary-400">{SAOP_MODAL.titleEmphasis}</span>
        {SAOP_MODAL.titlePost}
      </h3>
      <p className="mx-auto mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-300">
        {SAOP_MODAL.subtitle}
      </p>

      <div className="mx-auto mt-6 flex items-start justify-center gap-2 sm:gap-3">
        {SAOP_MODAL.workflow.map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && <span className="mt-3 text-gray-300 dark:text-gray-500">→</span>}
            <span className="flex w-16 sm:w-20 flex-col items-center gap-1.5">
              <Icon
                icon={WORKFLOW_ICONS[i]}
                className="h-7 w-7 text-primary-600 dark:text-primary-400"
              />
              <span className="text-[11px] leading-tight text-gray-600 dark:text-gray-300">
                {label}
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5 rounded-lg bg-primary-50 px-4 py-2.5 dark:bg-primary-900/20">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {SAOP_MODAL.banner}
        </p>
      </div>

      <p className="mt-5 text-lg font-bold text-gray-900 dark:text-gray-100">
        {SAOP_MODAL.leveragePre}
        <span className="text-primary-600 dark:text-primary-400">
          {SAOP_MODAL.leverageEmphasis}
        </span>
        {SAOP_MODAL.leveragePost}
      </p>

      <div className="mx-auto mt-4 flex items-center justify-center gap-2 sm:gap-3">
        <LetterChip letter="S" />
        <span className="text-gray-300 dark:text-gray-500">→</span>
        <span className="relative flex flex-col items-center">
          <span
            aria-hidden
            className="absolute -top-3.5 text-[10px] tracking-[0.35em] text-primary-500"
          >
            \|/
          </span>
          <LetterChip letter="A" highlight />
          <span className="absolute top-full mt-1 whitespace-nowrap text-[10px] font-semibold leading-tight text-primary-700 dark:text-primary-300">
            {SAOP_MODAL.letterLabel[0]}
            <br />
            {SAOP_MODAL.letterLabel[1]}
          </span>
        </span>
        <span className="text-gray-300 dark:text-gray-500">→</span>
        <LetterChip letter="O" />
        <span className="text-gray-300 dark:text-gray-500">→</span>
        <LetterChip letter="P" />
      </div>

      {/* Spacer so the absolute Assessment/Diagnosis label clears the row. */}
      <p className="mx-auto mt-10 max-w-md text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-primary-700 dark:text-primary-300">
          {SAOP_MODAL.wizardLead}
        </span>
        {SAOP_MODAL.wizardBody}
      </p>

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={onContinue}
          className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
        >
          {SAOP_MODAL.cta}
        </button>
      </div>

      <div className="mt-4 flex items-center justify-end gap-1.5 text-[11px] text-gray-400">
        <Icon icon={Icons.users} className="h-3.5 w-3.5" />
        {SAOP_MODAL.builtBy}
      </div>
    </div>
  </div>
);

export default SaopInterimModal;
